import React, { useEffect, useRef, useState } from 'react';
import { Zap, Shield, Users, Target, Award, TrendingUp } from 'lucide-react';
import './WhyChooseSFrays.css';

const reasons = [
  {
    icon: Zap,
    title: 'Rapid Innovation',
    description: 'Fast-track your digital transformation with agile methodologies and cutting-edge technologies.',
    metric: '3x',
    metricLabel: 'Faster Delivery',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Bank-level security protocols ensuring your data and systems are protected 24/7.',
    metric: '99.9%',
    metricLabel: 'Uptime SLA',
  },
  {
    icon: Users,
    title: 'Expert Team',
    description: 'Certified professionals with deep industry expertise and proven track records.',
    metric: '50+',
    metricLabel: 'Tech Experts',
  },
  {
    icon: Target,
    title: 'Results-Driven',
    description: 'Measurable outcomes aligned with your business goals and KPIs.',
    metric: '200+',
    metricLabel: 'Projects Delivered',
  },
  {
    icon: Award,
    title: 'Quality Assured',
    description: 'Rigorous testing and quality control at every stage of development.',
    metric: '98%',
    metricLabel: 'Client Satisfaction',
  },
  {
    icon: TrendingUp,
    title: 'Scalable Solutions',
    description: 'Future-proof architecture designed to grow with your business needs.',
    metric: '10x',
    metricLabel: 'Scalability',
  },
];

export function WhyChooseSFrays() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardsRef.current.indexOf(entry.target as HTMLDivElement);
            if (index !== -1 && !visibleCards.includes(index)) {
              setVisibleCards((prev) => [...prev, index]);
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, [visibleCards]);

  return (
    <section className="why-choose-sfrays section">
      <div className="why-choose-header">
        <h2 className="section-title">Why Choose SFrays</h2>
        <p className="section-subtitle">
          Partnering with SFrays means choosing excellence, innovation, and unwavering commitment to your success.
        </p>
      </div>

      <div className="why-choose-grid">
        {reasons.map((reason, index) => {
          const Icon = reason.icon;
          return (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className={`why-card ${visibleCards.includes(index) ? 'visible' : ''}`}
              style={{ animationDelay: `${(index % 3) * 0.1}s` }}
            >
              <div className="why-card-inner">
                <div className="why-icon-wrapper">
                  <div className="icon-glow"></div>
                  <Icon size={32} className="why-icon" />
                </div>

                <div className="why-metric">
                  <div className="metric-number">{reason.metric}</div>
                  <div className="metric-label">{reason.metricLabel}</div>
                </div>

                <h3 className="why-title">{reason.title}</h3>
                <p className="why-description">{reason.description}</p>

                <div className="card-shine"></div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
